import type { Thing, WebPage, WithContext } from 'schema-dts';
import type { Question } from '@/entities/question';
import { RegistrationType } from '@/entities/registration-type';
import type { AppBreadcrumbs } from '@/shared/components/shared/Breadcrumbs';
import { composeSchemaGraph } from '@/shared/lib/utils/page-json-ld';
import { oneLine, schemaIdRef, stripHtml } from '@/shared/lib/utils/schema-common';
import { getSchemaIds, schemaPageUrl } from '@/shared/lib/utils/schema-ids';

interface RegistrationJsonLdOptions {
    h1?: string;
    description?: string | null;
    registrationTypes: RegistrationType[];
    breadcrumbs?: AppBreadcrumbs | null;
    faqQuestions?: Question[] | null;
}

export function generateRegistrationJsonLd({
    h1,
    description,
    registrationTypes,
    breadcrumbs,
    faqQuestions,
}: RegistrationJsonLdOptions): WithContext<Thing>[] {
    const ids = getSchemaIds();
    const pageUrl = schemaPageUrl('/registration/');
    const offers = registrationTypes
        .map((type) => ({
            name: type.title,
            description: type.description || type.title,
            url: type.dashboardLink?.trim() || '',
        }))
        .filter((offer) => offer.url);

    const descriptionText = description ? oneLine(stripHtml(description)) : '';
    const webPage: WithContext<WebPage> = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${pageUrl}#webpage`,
        name: oneLine(h1 || 'Регистрация'),
        url: pageUrl,
        ...(descriptionText ? { description: descriptionText } : {}),
        isPartOf: schemaIdRef(ids.website) as WebPage['isPartOf'],
        publisher: schemaIdRef(ids.organization) as WebPage['publisher'],
        ...(offers.length > 0
            ? {
                  mainEntity: {
                      '@type': 'ItemList',
                      name: 'Типы регистрации',
                      numberOfItems: offers.length,
                      itemListElement: offers.map((offer, index) => ({
                          '@type': 'ListItem',
                          position: index + 1,
                          item: {
                              '@type': 'Offer',
                              name: offer.name,
                              ...(offer.description ? { description: oneLine(stripHtml(offer.description)) } : {}),
                              url: offer.url,
                              offeredBy: schemaIdRef(ids.organization),
                          },
                      })),
                  },
              }
            : {}),
    };

    return composeSchemaGraph({
        nodes: [webPage as WithContext<Thing>],
        breadcrumbs,
        faqQuestions,
        faqPageUrl: pageUrl,
    });
}
